import Coke from "assets/coke.svg";
import EdgeImpulse from "assets/edgeimpulse-logo.svg";
import Flowt from "assets/flowt.svg";
import Frase from "assets/frase.svg";
import Geospy from "assets/geospy.svg";
import Invoke from "assets/invoke-logo.svg";
import Jamie from "assets/jamie-logo.svg";
import Magellan from "assets/magellan.svg";
import Ogilvy from "assets/ogilvy-logo.svg";
import React from "react";
import ShippaboLogo from "assets/shippabo.svg";
import StratumLogo from "assets/stratum.svg";
import { Typography } from "@beamcloud/design-system";
import styled from "styled-components";
import { useMediaQuery } from "@mui/material";

const Logos = [
  {
    src: Coke,
    alt: "Coke",
    hasCaseStudy: false,
  },
  {
    src: Magellan,
    alt: "Magellan",
    hasCaseStudy: true,
    caseStudyUrl: "https://www.beam.cloud/customers/magellan-ai-case-study",
  },
  {
    src: StratumLogo,
    alt: "StratumAI",
    hasCaseStudy: false,
  },

  {
    src: Geospy,
    alt: "Geospy",
    hasCaseStudy: true,
    caseStudyUrl: "https://www.beam.cloud/customers/geospy-case-study",
  },
  {
    src: Ogilvy,
    alt: "Ogilvy",
    hasCaseStudy: false,
  },

  {
    src: Invoke,
    alt: "Invoke",
    hasCaseStudy: false,
  },
  {
    src: Frase,
    alt: "Frase",
    hasCaseStudy: false,
  },

  {
    src: Jamie,
    alt: "Jamie",
    hasCaseStudy: false,
  },
  {
    src: EdgeImpulse,
    alt: "EdgeImpulse",
    hasCaseStudy: false,
  },
  {
    src: ShippaboLogo,
    alt: "Shippabo",
    hasCaseStudy: false,
  },
];

interface IProps {}

const InvestorsCarousel: React.FC<IProps> = () => {
  const isNotFullScreen = useMediaQuery("(max-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const logoRows =
    isNotFullScreen && !isMobile
      ? [Logos.slice(0, 3), Logos.slice(3, 6), Logos.slice(6, 9), Logos.slice(9, 10)]
      : [Logos.slice(0, 5), Logos.slice(5, 10)];

  return (
    <Container>
      <TitleWrapper>
        <Typography tag="h6" variant="headingSm" color="gray11">
          TRUSTED BY THE BEST AI COMPANIES
        </Typography>
      </TitleWrapper>
      <LogoRowsContainer>
        {logoRows.map((row, index) => (
          <LogoRow key={index}>
            {row.map((logo, index) =>
              logo.hasCaseStudy ? (
                <CaseStudyLink key={index} href={logo.caseStudyUrl}>
                  <LogoContainer $hasCaseStudy={logo.hasCaseStudy}>
                    <InvestorLogo {...logo} $logoName={logo.alt} />
                    <CaseStudyBadge>Case study</CaseStudyBadge>
                  </LogoContainer>
                </CaseStudyLink>
              ) : (
                <LogoContainer key={index} $hasCaseStudy={logo.hasCaseStudy}>
                  <InvestorLogo {...logo} $logoName={logo.alt} />
                </LogoContainer>
              )
            )}
          </LogoRow>
        ))}
      </LogoRowsContainer>
    </Container>
  );
};

export default InvestorsCarousel;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-top: 32px;
  width: 100%;
`;

const TitleWrapper = styled.div`
  width: 100%;
  text-align: center;
  padding: 0 16px;
  max-width: 1200px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0 12px;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
  }
`;

const LogoRowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  position: relative;
  overflow: hidden;
`;

const LogoRowWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const LogoRow = styled.div`
  display: flex;
  gap: 42px;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  width: 100%;
  padding: 0 24px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 16px;
    padding: 0 12px;
  }

  @media (max-width: 480px) {
    gap: 12px;
    padding: 0 8px;
  }
`;

const FadeOverlay = styled.div<{ position: "left" | "right" }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 200px;
  z-index: 10;
  pointer-events: none;
  background: ${({ position, theme }) =>
    position === "left"
      ? `linear-gradient(to right, ${theme.colors.gray1}, ${theme.colors.gray1}80, transparent)`
      : `linear-gradient(to left, ${theme.colors.gray1}, ${theme.colors.gray1}80, transparent)`};
  ${({ position }) => position}: 0;
`;

const InvestorLogoGroup = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 84px;
  left: 0;
  bottom: -20px;
`;

const LogoContainer = styled.div<{ $hasCaseStudy: boolean }>`
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  cursor: ${({ $hasCaseStudy }) => ($hasCaseStudy ? "pointer" : "default")};
  transition: opacity 0.2s ease;
  z-index: 10;
  position: relative;

  &:hover {
    opacity: ${({ $hasCaseStudy }) => ($hasCaseStudy ? 0.8 : 1)};
  }

  @media (max-width: 768px) {
    width: 80px;
    padding: 4px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    width: 60px;
    padding: 2px;
  }
`;

const InvestorLogo = styled.img<{ $logoName: string }>`
  max-width: 100%;
  max-height: 80px;
  object-fit: contain;
  transform: ${({ $logoName }) => {
    switch ($logoName) {
      case "Coke":
        return "scale(1.5)";
      case "Jamie":
        return "scale(0.85)";
      case "Ogilvy":
        return "scale(0.65)";
      case "Frase":
      case "EdgeImpulse":
        return "scale(1.1)";
      case "StratumAI":
        return "scale(1.2)";
      case "Shippabo":
        return "scale(1.2)";
      default:
        return "scale(1)";
    }
  }};

  @media (max-width: 768px) {
    max-height: 60px;
  }

  @media (max-width: 480px) {
    max-height: 40px;
  }
`;

const CaseStudyLink = styled.a`
  text-decoration: none;
  color: inherit;
`;

const CaseStudyBadge = styled.span`
  background-color: #e6f3ff;
  color: #1a1a1a;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  @media (max-width: 768px) {
    display: none;
  }
`;
